import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/common";
import { MapPin, Crosshair, Loader2, Search, ShieldAlert, ShieldCheck, ShieldQuestion, ExternalLink, Plus, RefreshCw, X } from "@/components/icons";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const icon = (color) =>
  L.divIcon({
    className: "",
    html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.4)"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
const ICON_NOVO = icon("#008AB0");
const ICON_CAD = icon("#94A3B8");
const ICON_ME = icon("#B42318");

function Recenter({ pos }) {
  const map = useMap();
  useEffect(() => { if (pos) map.setView(pos, 14); }, [pos, map]);
  return null;
}

export default function Prospectar() {
  const { user } = useAuth();
  const canDirect = ["admin", "director", "manager"].includes(user?.role);
  const [tipos, setTipos] = useState([]);
  const [tipo, setTipo] = useState("hotel");
  const [raio, setRaio] = useState(3000);
  const [pos, setPos] = useState(null);
  const [locating, setLocating] = useState(false);
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);
  const [vendedores, setVendedores] = useState([]);
  const [vendedorId, setVendedorId] = useState("");
  const [rep, setRep] = useState({}); // osm_id -> {loading, data}
  const [openRep, setOpenRep] = useState(null);
  const [filtro, setFiltro] = useState(null); // null | "reclamacoes" | "enxoval"
  const [addLead, setAddLead] = useState(null); // item selecionado para "Adicionar como potencial cliente"
  const [addCnpj, setAddCnpj] = useState("");
  const [addBusy, setAddBusy] = useState(false);

  useEffect(() => {
    api.get("/prospeccao/tipos").then((r) => setTipos(r.data || [])).catch(() => {});
    if (canDirect) api.get("/prospeccao/vendedores").then((r) => { setVendedores(r.data || []); const me = (r.data || []).find((v) => v.eu); if (me) setVendedorId(me.id); }).catch(() => {});
  }, [canDirect]);

  const locate = () => {
    if (!navigator.geolocation) return toast.error("Geolocalização não suportada neste navegador");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setPos([p.coords.latitude, p.coords.longitude]); setLocating(false); toast.success("Localização obtida"); },
      () => { setLocating(false); toast.error("Não foi possível obter sua localização. Verifique a permissão do navegador."); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const [buscaErro, setBuscaErro] = useState(null); // null | "vazio" | "timeout" | "erro"
  const buscar = async () => {
    if (!pos) return toast.error("Obtenha sua localização primeiro");
    setSearching(true);
    setBuscaErro(null);
    try {
      // Timeout de 20s (item 8 do CORRECAO_EMERGENT_FLUXO_FUNIL).
      const { data } = await api.get("/prospeccao/buscar", {
        params: { lat: pos[0], lng: pos[1], tipo, raio },
        timeout: 20000,
      });
      setResult(data);
      if (!data.items.length) setBuscaErro("vazio");
    } catch (err) {
      const timedOut = err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "");
      setBuscaErro(timedOut ? "timeout" : "erro");
      setResult(null);
    } finally { setSearching(false); }
  };

  const criarLead = (item) => { setAddLead(item); setAddCnpj(""); };
  const confirmarLead = async () => {
    if (!/^\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}$/.test(addCnpj.trim())) return toast.error("Informe um CNPJ válido (14 dígitos)");
    setAddBusy(true);
    try {
      await api.post("/prospeccao/lead", {
        nome: addLead.nome, cnpj: addCnpj.trim(),
        telefone: addLead.telefone, endereco: addLead.endereco, site: addLead.site,
        lat: addLead.lat, lng: addLead.lng, categoria: addLead.categoria, osm_id: addLead.osm_id,
        vendedor_id: canDirect ? vendedorId : undefined,
      });
      toast.success(`${addLead.nome} adicionado como prospect`);
      setAddLead(null); setAddCnpj("");
      buscar();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setAddBusy(false); }
  };

  const verificarReputacao = async (item, force = false) => {
    setRep((r) => ({ ...r, [item.osm_id]: { ...(r[item.osm_id] || {}), loading: true } }));
    try {
      const { data } = await api.post("/prospeccao/reputacao", {
        nome: item.nome, endereco: item.endereco, osm_id: item.osm_id,
        categoria_tipo: tipo, force,
      });
      setRep((r) => ({ ...r, [item.osm_id]: { loading: false, data } }));
      setOpenRep(item.osm_id);
    } catch (err) {
      setRep((r) => ({ ...r, [item.osm_id]: { loading: false } }));
      toast.error(errMsg(err));
    }
  };

  const totalReclamacoes = (result?.items || []).filter((it) => rep[it.osm_id]?.data?.tem_alerta).length;
  const totalEnxoval = (result?.items || []).filter((it) => (rep[it.osm_id]?.data?.contagem?.Enxoval || 0) > 0).length;
  const itemsFiltrados = () => {
    const items = result?.items || [];
    if (!filtro) return items;
    return items.filter((it) => {
      const d = rep[it.osm_id]?.data;
      if (!d?.tem_alerta) return false;
      if (filtro === "enxoval") return (d.contagem?.Enxoval || 0) > 0;
      return true;
    });
  };

  return (
    <div>
      <PageHeader title="Buscar no mapa" subtitle="Encontre estabelecimentos perto de você" />

      <div className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] mb-5">
        <div className="grid md:grid-cols-4 gap-3">
          <button onClick={locate} disabled={locating} data-testid="locate-btn" className="h-11 rounded-lg bg-navy text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-navy/90 disabled:opacity-60">
            {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />} Minha localização
          </button>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="tipo-select" className="h-11 px-3.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40">
            {tipos.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <select value={raio} onChange={(e) => setRaio(Number(e.target.value))} data-testid="raio-select" className="h-11 px-3.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40">
            {[1000, 2000, 3000, 5000, 10000].map((r) => <option key={r} value={r}>{r / 1000} km</option>)}
          </select>
          <button onClick={buscar} disabled={searching || !pos} data-testid="buscar-btn" className="h-11 rounded-lg bg-brand text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-brand-hover disabled:opacity-60">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Buscar
          </button>
        </div>
        {canDirect && vendedores.length > 0 && (
          <div className="mt-3">
            <label className="text-xs font-medium text-slate-600">Direcionar novos prospects para:</label>
            <select value={vendedorId} onChange={(e) => setVendedorId(e.target.value)} data-testid="vendedor-select" className="mt-1 w-full md:w-64 h-10 px-3 rounded-lg border border-input bg-white text-sm">
              {vendedores.map((v) => <option key={v.id} value={v.id}>{v.name}{v.eu ? " (eu)" : ""}</option>)}
            </select>
          </div>
        )}
      </div>

      {pos && (
        <div className="rounded-2xl overflow-hidden border border-black/5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] mb-5" style={{ height: 360 }}>
          <MapContainer center={pos} zoom={14} style={{ height: "100%", width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
            <Recenter pos={pos} />
            <Marker position={pos} icon={ICON_ME}><Popup>Você está aqui</Popup></Marker>
            {(result?.items || []).map((it) => (
              <Marker key={it.osm_id} position={[it.lat, it.lng]} icon={it.ja_cadastrado ? ICON_CAD : ICON_NOVO}>
                <Popup>
                  <div className="text-sm">
                    <div className="font-semibold text-navy">{it.nome}</div>
                    <div className="text-xs text-slate-500">{it.endereco || it.categoria} · {it.distancia_m}m</div>
                    {it.ja_cadastrado ? (
                      <div className="text-xs text-slate-500 mt-1">Já cadastrado ({it.ja_cadastrado.owner_name})</div>
                    ) : (
                      <button onClick={() => criarLead(it)} className="mt-2 text-xs font-semibold text-brand">+ Criar prospect</button>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}

      {searching && (
        <div className="mb-3 rounded-lg border border-black/5 bg-brand-soft/40 px-4 py-3 text-sm text-navy flex items-center gap-2" data-testid="prospectar-buscando">
          <Loader2 className="w-4 h-4 animate-spin" /> Buscando…
        </div>
      )}
      {!searching && buscaErro === "vazio" && (
        <div className="mb-3 rounded-lg border border-black/5 bg-slate-50 px-4 py-3 text-sm text-muted-foreground" data-testid="prospectar-vazio">
          Nenhum estabelecimento encontrado neste raio. Tente aumentar o raio ou outra categoria.
        </div>
      )}
      {!searching && buscaErro === "timeout" && (
        <div className="mb-3 rounded-lg border border-warn/40 bg-warn-soft px-4 py-3 text-sm text-warn" data-testid="prospectar-timeout">
          A busca demorou demais. Tente de novo.
        </div>
      )}
      {!searching && buscaErro === "erro" && (
        <div className="mb-3 rounded-lg border border-bad/40 bg-bad-soft px-4 py-3 text-sm text-bad" data-testid="prospectar-erro">
          Não conseguimos buscar agora. Tente de novo.
        </div>
      )}

      {result && (
        <div>
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <h3 className="text-sm font-semibold text-navy">{result.label} · {result.count} encontrados</h3>
            <span className="text-xs text-muted-foreground">{result.novos} novos</span>
          </div>
          {(totalReclamacoes > 0 || totalEnxoval > 0) && (
            <div className="flex flex-wrap gap-2 mb-3" data-testid="rep-chips">
              <button onClick={() => setFiltro(filtro === "reclamacoes" ? null : "reclamacoes")}
                data-testid="chip-reclamacoes"
                className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors flex items-center gap-1.5 ${filtro === "reclamacoes" ? "bg-bad text-white border-bad" : "bg-white text-bad border-bad/30 hover:bg-bad-soft"}`}>
                <ShieldAlert className="w-3.5 h-3.5" /> Com reclamações ({totalReclamacoes})
                {filtro === "reclamacoes" && <X className="w-3 h-3" />}
              </button>
              {tipo === "hotel" && totalEnxoval > 0 && (
                <button onClick={() => setFiltro(filtro === "enxoval" ? null : "enxoval")}
                  data-testid="chip-enxoval"
                  className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors flex items-center gap-1.5 ${filtro === "enxoval" ? "bg-warn text-white border-warn" : "bg-white text-warn border-warn/40 hover:bg-warn-soft"}`}>
                  <ShieldAlert className="w-3.5 h-3.5" /> Enxoval ({totalEnxoval})
                  {filtro === "enxoval" && <X className="w-3 h-3" />}
                </button>
              )}
            </div>
          )}
          <div className="space-y-2">
            {itemsFiltrados().map((it) => {
              const r = rep[it.osm_id];
              const d = r?.data;
              const isOpen = openRep === it.osm_id;
              // 4 estados de selo:
              //  - "indef"    : ainda não verificado (cinza — "Higiene não verificada")
              //  - "alerta"   : encontrou pelo menos 1 reclamação (vermelho)
              //  - "limpo"    : pelo menos 1 fonte respondeu OK e nada foi achado (verde)
              //  - "falhou"   : todas as fontes falharam (amarelo — "Não foi possível verificar")
              // Nunca mostrar verde sem ter conseguido consultar nenhuma fonte.
              const statusRep = !d ? "indef"
                : (d.status || (d.tem_alerta ? "com_reclamacao"
                   : ((d.fontes_verificadas || []).length > 0 ? "sem_reclamacao" : "falhou")));
              const selo = statusRep === "com_reclamacao" ? "alerta"
                : statusRep === "sem_reclamacao" ? "limpo"
                : statusRep === "falhou" ? "falhou"
                : "indef";
              const enxN = d?.contagem?.Enxoval || 0;
              const limN = d?.contagem?.Limpeza || 0;
              const totalAval = d?.total_avaliacoes || 0;
              const rotulo = !d ? "Higiene não verificada"
                : selo === "alerta"
                  ? (tipo === "hotel" && enxN > 0
                      ? `Enxoval ${enxN}${limN > 0 ? ` · Limpeza ${limN}` : ""}`
                      : `${enxN + limN} reclamaç${(enxN + limN) === 1 ? "ão" : "ões"} de higiene`)
                  : selo === "limpo"
                    ? "Sem reclamação de higiene"
                    : selo === "falhou"
                      ? "Não foi possível verificar"
                      : "Higiene não verificada";
              return (
                <div key={it.osm_id} className="bg-card rounded-xl border border-black/5 p-4" data-testid={`prospect-${it.osm_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-navy flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-brand" />{it.nome}</div>
                      <div className="text-xs text-muted-foreground">{it.endereco || it.categoria} · {it.distancia_m}m{it.telefone ? ` · ${it.telefone}` : ""}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      {it.ja_cadastrado ? (
                        <span className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded-full">Cadastrado</span>
                      ) : (
                        <button onClick={() => criarLead(it)} data-testid={`lead-${it.osm_id}`} className="flex items-center gap-1 text-xs font-semibold text-white bg-brand hover:bg-brand-hover px-2.5 py-1.5 rounded-lg"><Plus className="w-3.5 h-3.5" /> Prospect</button>
                      )}
                      {/* Selo pílula (Mensagem 6 — Reputação objetiva) */}
                      {r?.loading ? (
                        <span className="text-xs bg-slate-100 text-slate-500 rounded-full px-2.5 py-1 flex items-center gap-1.5 animate-pulse" data-testid={`rep-selo-loading-${it.osm_id}`}>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Verificando…
                        </span>
                      ) : (
                        <button
                          onClick={() => (d ? setOpenRep(isOpen ? null : it.osm_id) : verificarReputacao(it))}
                          data-testid={`rep-selo-${it.osm_id}`}
                          className={`text-xs font-medium rounded-full px-2.5 py-1 flex items-center gap-1.5 transition-colors ${
                            selo === "alerta" ? "bg-bad-soft text-bad hover:bg-bad-soft/70"
                            : selo === "limpo" ? "bg-ok-soft text-ok hover:bg-ok-soft/70"
                            : selo === "falhou" ? "bg-warn-soft text-warn hover:bg-warn-soft/70"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          {selo === "alerta" ? <ShieldAlert className="w-3.5 h-3.5" />
                            : selo === "limpo" ? <ShieldCheck className="w-3.5 h-3.5" />
                            : selo === "falhou" ? <ShieldAlert className="w-3.5 h-3.5" />
                            : <ShieldQuestion className="w-3.5 h-3.5" />}
                          {rotulo}
                        </button>
                      )}
                    </div>
                  </div>
                  {isOpen && d && (
                    <div className="mt-3 rounded-lg border border-black/5 bg-slate-50/60 p-3 text-xs" data-testid={`rep-painel-${it.osm_id}`}>
                      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                        <div className="flex items-center gap-2 text-slate-600 flex-wrap">
                          {(d.fontes_verificadas || []).map((f) => (
                            <span key={f.key} className="text-[11px]">{f.nome}</span>
                          )).reduce((acc, el, i, arr) => acc.concat(i < arr.length - 1 ? [el, <span key={`sep-${i}`} className="text-slate-300">·</span>] : [el]), [])}
                          {(d.fontes_indisponiveis || []).map((f) => (
                            <span key={f.key} className="text-[11px] text-slate-400 line-through" title="indisponível">{f.nome}</span>
                          ))}
                          <span className="text-[11px] text-slate-400">— verificado em {new Date(d.verificado_em).toLocaleDateString("pt-BR")}</span>
                        </div>
                        <button onClick={() => verificarReputacao(it, true)} data-testid={`rep-refresh-${it.osm_id}`} className="text-slate-500 hover:text-navy" title="Verificar novamente">
                          <RefreshCw className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {(d.reclamacoes || []).length > 0 ? (
                        <div className="space-y-2">
                          {d.reclamacoes.map((rec, i) => (
                            <div key={i} className={`flex gap-2 rounded-md bg-white p-2.5 border-l-2 ${rec.categoria === "Enxoval" ? "border-warn" : "border-bad"}`} data-testid={`rec-${it.osm_id}-${i}`}>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-[10px] uppercase tracking-wider font-semibold rounded px-1.5 py-0.5 ${rec.categoria === "Enxoval" ? "bg-warn/15 text-warn" : "bg-bad/15 text-bad"}`}>{rec.categoria}</span>
                                  {rec.termo && <span className="text-[10px] text-slate-400">termo: <span className="font-medium text-navy">{rec.termo}</span></span>}
                                </div>
                                <p className="italic text-slate-700 leading-snug">
                                  {"\u201C"}{rec.trecho.split(new RegExp(`(${rec.termo})`, "i")).map((part, k) => (
                                    part.toLowerCase() === (rec.termo || "").toLowerCase()
                                      ? <mark key={k} className="bg-yellow-200 rounded px-0.5">{part}</mark>
                                      : part
                                  ))}{"\u201D"}
                                </p>
                                <a href={rec.url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-brand hover:underline text-[11px]">
                                  <ExternalLink className="w-3 h-3" /> {rec.fonte_nome}
                                </a>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : selo === "falhou" ? (
                        <div className="rounded-md bg-warn-soft/40 border border-warn/30 p-2.5 text-warn text-[11px]" data-testid={`rep-falhou-${it.osm_id}`}>
                          <p className="font-semibold mb-1">Não foi possível verificar agora.</p>
                          <p className="text-slate-700 italic">Isso <strong>não</strong> quer dizer que o estabelecimento está sem reclamação. As fontes {(d.fontes_indisponiveis || []).map((f) => f.nome).join(", ") || "consultadas"} não responderam. Tente novamente em alguns minutos.</p>
                        </div>
                      ) : (
                        <p className="text-slate-500 text-[11px] text-center py-2" data-testid={`rep-limpo-${it.osm_id}`}>
                          Nenhuma reclamação de higiene · em {totalAval} avaliaç{totalAval === 1 ? "ão" : "ões"} lida{totalAval === 1 ? "" : "s"} nas fontes {(d.fontes_verificadas || []).map((f) => f.nome).join(", ")}.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={!!addLead} onOpenChange={(o) => !o && setAddLead(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar como prospect</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            <b>{addLead?.nome}</b> — informe o CNPJ para reservar o prospect por 90 dias. Sem CNPJ o cadastro não é aceito.
          </p>
          <input
            value={addCnpj}
            onChange={(e) => setAddCnpj(e.target.value)}
            placeholder="00.000.000/0000-00"
            data-testid="prospectar-cnpj-input"
            className="w-full h-11 px-3.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40"
            autoFocus
          />
          <DialogFooter>
            <button onClick={() => setAddLead(null)} className="h-10 px-4 rounded-lg border border-input text-sm hover:bg-slate-50">Cancelar</button>
            <button
              onClick={confirmarLead}
              disabled={addBusy || !addCnpj.trim()}
              data-testid="prospectar-cnpj-confirm"
              className="h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-60 flex items-center gap-2"
            >
              {addBusy && <Loader2 className="w-4 h-4 animate-spin" />} Adicionar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
